import { motion, AnimatePresence } from "framer-motion"
import { useEffect, useRef } from 'react'
import { useTranslation } from "react-i18next";
import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";
import { getNotificationCopy } from "../../utils/notificationCopy";

const NotificationItem = ({ notification, formatDate, isDarkTheme, onClose }) => {
    const { t } = useTranslation("profile")
    const { t: tNotification } = useTranslation("notifications")
    const markNotificationAsRead = useAppStore(state => state.markNotificationAsRead)
    const setActiveSection = useAppStore(state => state.setActiveSection)
    const setSelectedShiftConfirmationNotificationId = useAppStore(state => state.setSelectedShiftConfirmationNotificationId)
    const employeesData = useAppStore(state => state.employeesData)
    const { title, message } = getNotificationCopy(notification, tNotification, { employeesData })

    return (
        <motion.div
            key={notification._id}
            className={`nmc-notification-item ${!notification.read ? 'nmc-notification-unread' : 'nmc-notification-read'}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
                console.log(notification.relatedData);
                markNotificationAsRead(notification._id);

                const type = notification.type;
                if (type === 'auto_logout') {
                    setActiveSection('Statistika');
                } else if (type === 'employee_login') {
                    setActiveSection('Prisutnost');
                } else if (type === 'shift_confirmation_request') {
                    setActiveSection('Prisutnost');
                    setSelectedShiftConfirmationNotificationId(notification._id);
                } else if (typeof type === 'string' && type.includes('absence')) {
                    setActiveSection('Kalendar');
                }

                onClose();
            }}
        >
            <div className="nmc-notification-header">
                <div className="nmc-notification-title">{title}</div>
                {!notification.read && (
                    <div className="nmc-notification-dot"></div>
                )}
            </div>
            <div className="nmc-notification-message">{message}</div>
            <div className="nmc-notification-time">{formatDate(notification.createdAt)}</div>
            <AnimatePresence>
                {!notification.read && (
                    <motion.button
                        type="button"
                        className={`nmc-mark-as-read-button ${isDarkTheme ? 'nmc-mark-as-read-button-dark' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            markNotificationAsRead(notification._id);
                        }}
                        aria-label={t("notifications.markReadAria")}
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 0.75, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.75, y: 6 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                        <img
                            src="./sidebar/MarkAsRead.svg"
                            alt=""
                            aria-hidden="true"
                            className={isDarkTheme ? "filter-dark-buttons" : ""}
                        />
                    </motion.button>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const NotificationModal = ({ onClose }) => {
    const { t, i18n } = useTranslation("profile")
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const notifications = useAppStore(state => state.notifications)
    const markAllNotificationsAsRead = useAppStore(state => state.markAllNotificationsAsRead)
    const modalContainerRef = useRef(null)
    const hasUnreadNotifications = notifications.some((notification) => !notification.read)

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Ako je klik na modal container, ne zatvaraj modal
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }

            // Ako je klik na dugme koje otvara modal, ne zatvaraj modal
            const notificationButton = e.target.closest('.notification-button');
            if (notificationButton) {
                return;
            }

            onClose();
        };

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [onClose]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t("notifications.justNow");
        if (diffMins < 60) return t("notifications.minutesAgo", { count: diffMins });
        if (diffHours < 24) return t("notifications.hoursAgo", { count: diffHours });
        if (diffDays < 7) return t("notifications.daysAgo", { count: diffDays });

        const locale = i18n.language === "en" ? "en-GB" : "sr-RS";
        return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    };

    return (
        <>
            <motion.div
                ref={modalContainerRef}
                className={`notification-modal-container ${isDarkTheme ? "dark-notification-modal" : "white-notification-modal"}`}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -14 }}
                transition={{ duration: 0.19, ease: "easeInOut" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="notification-modal-header-buttons">
                    {hasUnreadNotifications && (
                        <button
                            type="button"
                            className={`notification-modal-mark-all-read ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                            aria-label={t("notifications.markAllReadAria")}
                            onClick={() => {
                                markAllNotificationsAsRead();
                            }}
                        >
                            <img src="./sidebar/MarkAsRead.svg" alt="" />
                        </button>
                    )}
                    <button
                        className={`notification-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                        onClick={onClose}
                        aria-label={t("notifications.closeAria")}
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ display: "block", margin: "auto" }}
                        >
                            <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                            <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
                <div className="notification-modal-content">
                    <div className="nmc-title-container">
                        {t("notifications.title")}
                    </div>
                    <div className="nmc-list-container">
                        {notifications.length === 0 ? (
                            <div className="nmc-empty-state">
                                {t("notifications.empty")}
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <NotificationItem
                                    key={notification._id}
                                    notification={notification}
                                    formatDate={formatDate}
                                    isDarkTheme={isDarkTheme}
                                    onClose={onClose}
                                />
                            ))
                        )}
                    </div>
                </div>
            </motion.div>
            <motion.div
                className={`notification-modal-bg-wrapper ${isDarkTheme ? "dark-notification-modal-bg-wrapper" : "white-notification-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
            </motion.div>
        </>
    )
}

export default NotificationModal
